/**
 * 모니터링 ▸ 사업장 현황의 **그림판(draw)** — 모니터링의 기본 보기다(2026-10-22 사용자 지시 "이전의 draw 타입의 모니터링을
 * 기본으로 사용한다"). 지도(`SiteMap.tsx`)는 다른 서버와 연동된 서버의 **통합 모니터링**으로 따로 둔다.
 *
 * 이 서버의 모든 지역을 한 장(월드)에 그려 두고 — 지역 영역 안에 공장 영역, 공장 영역 안에 건물별로 화면 노드 — 사람은
 * 끌어서 옮기고 휠·핀치·단추로 키우고 줄이며 본다(카메라 셈은 `camera.ts`). 나무·영역 제목·노드에서 범위를 고르면 카메라가
 * 그 영역으로 날아가 맞춘다(떠 있는 판 — 메뉴·도구 줄·화면 판 — 이 가린 가장자리는 빼고).
 *
 * [배치 편집] 을 켜면 지역·공장·건물 영역의 머리줄을 끌어 옮기고 오른쪽 아래 손잡이로 크기를 바꾼다(키보드 Alt+화살표
 * 이동, Alt+Shift+화살표 크기). **자식은 부모 밖으로 나가지 않고, 부모는 자식보다 작아지지 않는다** — 셈은
 * `siteLayout.ts`, 배치는 계정별로 저장한다(`/api/v1/me/site-layout`).
 *
 * 2026-10-22 지도로 바꾸며 걷었다가 같은 날 기본 보기로 되살렸다 — 고른 화면의 판은 이제 부르는 쪽(`screens/Sites.tsx`)이
 * 그린다(지도와 같은 판).
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Building, Building2, MapPin, Maximize, Minus, Move, Plus, RotateCcw, WifiOff } from 'lucide-react';
import type { SignageOneClient } from '../api/client.js';
import { useAsync } from '../hooks.js';
import { relativeTime } from './ui.js';
import {
  fitRect,
  panBy,
  pinch,
  wheelFactor,
  zoomAt,
  ZOOM_STEP,
  type Camera,
  type Insets,
  type Rect,
} from './camera.js';
import {
  buildingMinHeight,
  clampMove,
  clampResize,
  computeSiteLayout,
  HEAD,
  MIN_W,
  minSizeAround,
  type Box,
  type LaidBuilding,
  type LaidFactory,
  type LaidRegion,
  type SavedBoxes,
} from '../siteLayout.js';
import {
  healthTone,
  type Counts,
  type RegionNode,
  type ResolvedScope,
  type SiteDisplay,
  type SiteScope,
} from '../sites.js';
import { CountLine, DisplayNode, HealthBar, sourceLabel } from './siteParts.js';
import { useSiteLabels } from './siteLabelsContext.js';
import { unnamedRegion } from '../siteLabels.js';

/** 범위마다 맞출 때 키울 수 있는 끝 — 화면 하나를 골라도 노드가 판을 덮지 않게. */
const FOCUS_MAX_K: Record<SiteScope['kind'], number> = { all: 1, region: 1.1, factory: 1.3, display: 1.6 };

function focusIdOf(resolved: ResolvedScope): string | null {
  if (resolved.display) return `d:${resolved.display.key}`;
  if (resolved.factory) return `f:${resolved.factory.key}`;
  if (resolved.region) return `r:${resolved.region.key}`;
  return null;
}

/** 월드 안에서의 자리 — 카메라의 `transform` 과 상관없는 offset 으로 잰다(날아가는 도중에 재도 맞다). */
function rectInWorld(element: HTMLElement, world: HTMLElement): Rect {
  let x = 0;
  let y = 0;
  let node: HTMLElement | null = element;
  while (node && node !== world) {
    x += node.offsetLeft;
    y += node.offsetTop;
    node = node.offsetParent as HTMLElement | null;
  }
  return { x, y, width: element.offsetWidth, height: element.offsetHeight };
}

const WIDE = '(min-width: 1024px)';

/** 영역 하나의 한계 — 부모 안쪽(없으면 그림)과 자기 내용의 최소 크기, 그리고 굳힐 조상들. */
interface AreaRule {
  box: Box;
  parent: { w: number; h: number; head: number } | null;
  minW: number;
  minHeightFor: (width: number) => number;
  ancestors: Array<{ key: string; box: Box }>;
}

/** 배치 편집의 손잡이 — 영역이 머리줄(옮기기)과 모서리(크기)에 단다. 편집 중이 아니면 null. */
interface AreaEdit {
  start: (key: string, mode: 'move' | 'resize', event: React.PointerEvent) => void;
  nudge: (key: string, event: React.KeyboardEvent) => void;
}

const boxStyle = (box: Box): React.CSSProperties => ({ left: box.x, top: box.y, width: box.w, height: box.h });

/**
 * 꽉 찬 그림판 — 모든 지역을 한 장(월드)에 그려 두고 카메라로 본다. 끌면 옮기고, 휠·트랙패드 핀치·두 손가락
 * 핀치·[−][+] 로 커서(가운데)를 붙든 채 키우고 줄인다. 고른 범위가 바뀌면 그 영역으로 날아가 맞춘다 — 떠 있는
 * 판(메뉴·도구 줄·화면 판)이 가린 가장자리는 빼고.
 */
export default function SiteBoard({
  client,
  tree,
  resolved,
  flights,
  settled,
  onSelect,
  canEdit,
  onConfigure,
}: {
  client: SignageOneClient;
  tree: RegionNode[];
  resolved: ResolvedScope;
  /** 고른 횟수 — 같은 범위를 다시 골라도 다시 맞춘다. */
  flights: number;
  /** 처음 받을 것을 다 받았다 — 처음 맞추기를 그때 한다. */
  settled: boolean;
  onSelect: (scope: SiteScope) => void;
  canEdit: boolean;
  onConfigure: () => void;
}): ReactNode {
  const viewRef = useRef<HTMLDivElement>(null);
  const worldRef = useRef<HTMLDivElement>(null);
  const [camera, setCamera] = useState<Camera>({ x: 0, y: 0, k: 1 });
  /** 마지막으로 맞춘 카메라 — 지금 카메라가 이것 그대로면 사람이 아직 끌거나 키우지 않았다. */
  const fitted = useRef<Camera | null>(null);
  const cameraNow = useRef(camera);
  cameraNow.current = camera;
  const [flying, setFlying] = useState(false);
  const [placed, setPlaced] = useState(false);
  const [panning, setPanning] = useState(false);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const drag = useRef<{ start: { x: number; y: number }; last: { x: number; y: number }; moved: boolean } | null>(null);
  /** 끌기를 끝낸 시각 — 그 직후의 클릭(손을 뗀 자리의 노드)만 삼킨다. 키보드 누르기는 삼키지 않는다. */
  const draggedAt = useRef(0);
  const flyTimer = useRef<number | undefined>(undefined);

  /*
   * 배치 — 사람이 옮기거나 키운 영역(계정별). 받기 전에는 그리지 않는다(자동 배치로 맞춘 뒤 저장값이 오면 그림이
   * 튄다). 못 받으면 자동 배치로 간다.
   */
  const savedLayout = useAsync(() => client.siteLayout(), [client]);
  const [boxes, setBoxes] = useState<SavedBoxes | null>(null);
  useEffect(() => {
    if (boxes !== null) return;
    if (savedLayout.data)
      setBoxes(
        Object.fromEntries(
          savedLayout.data.items.map((item) => [item.key, { x: item.x, y: item.y, w: item.w, h: item.h }]),
        ),
      );
    else if (savedLayout.error) setBoxes({});
  }, [savedLayout.data, savedLayout.error, boxes]);
  const layout = useMemo(() => computeSiteLayout(tree, boxes ?? {}), [tree, boxes]);
  const [editing, setEditing] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const saveTimer = useRef<number | undefined>(undefined);
  const persist = (next: SavedBoxes, delay = 0): void => {
    window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      client
        .saveSiteLayout(Object.entries(next).map(([key, box]) => ({ key, ...box })))
        .then(() => setSaveError(null))
        .catch((error: unknown) => setSaveError(error instanceof Error ? error.message : String(error)));
    }, delay);
  };
  useEffect(() => () => window.clearTimeout(saveTimer.current), []);

  /** 영역마다의 한계 — 지금 그림에서 센다. 끌기는 시작할 때 한 번 집어 끝까지 쓴다(그동안 조상은 굳는다). */
  const rules = useMemo(() => {
    const map = new Map<string, AreaRule>();
    for (const region of layout.regions) {
      const regionMin = minSizeAround(
        region.factories.map((item) => item.box),
        'region',
      );
      map.set(region.key, {
        box: region.box,
        parent: null,
        minW: regionMin.w,
        minHeightFor: () => regionMin.h,
        ancestors: [],
      });
      for (const factory of region.factories) {
        const factoryMin = minSizeAround(
          factory.buildings.map((item) => item.box),
          'factory',
        );
        const up = [{ key: region.key, box: region.box }];
        map.set(factory.key, {
          box: factory.box,
          parent: { w: region.box.w, h: region.box.h, head: HEAD.region },
          minW: factoryMin.w,
          minHeightFor: () => factoryMin.h,
          ancestors: up,
        });
        for (const building of factory.buildings) {
          map.set(building.key, {
            box: building.box,
            parent: { w: factory.box.w, h: factory.box.h, head: HEAD.factory },
            minW: MIN_W.building,
            minHeightFor: (width) => buildingMinHeight(building.building, width),
            ancestors: [...up, { key: factory.key, box: factory.box }],
          });
        }
      }
    }
    return map;
  }, [layout]);

  const fit = (rule: AreaRule, mode: 'move' | 'resize', candidate: Box, previous: Box): Box =>
    mode === 'move'
      ? clampMove(candidate, rule.parent)
      : clampResize(candidate, { w: rule.minW, minHeightFor: rule.minHeightFor }, rule.parent, previous);

  /**
   * 끌기를 시작할 때 **조상들을 지금 크기로 굳힌다.** 자동 크기인 부모는 자식을 감싸는 크기라, 굳히지 않으면 자식을
   * 위로 옮길수록 부모가 줄어 다시 내려올 자리가 없어진다. "부모 밖으로 못 나간다" 는 그 굳힌 크기에 대해서다.
   */
  const freeze = (rule: AreaRule, key: string): SavedBoxes => {
    const next = { ...(boxes ?? {}) };
    for (const ancestor of rule.ancestors) next[ancestor.key] = ancestor.box;
    next[key] = rule.box;
    return next;
  };

  const edit: AreaEdit | null = editing
    ? {
        start: (key, mode, event) => {
          if (event.pointerType === 'mouse' && event.button !== 0) return;
          const rule = rules.get(key);
          if (!rule) return;
          // 그림판이 이것을 끌기(이동)로 받지 않게.
          event.stopPropagation();
          event.preventDefault();
          const state = { working: freeze(rule, key), moved: false, x: event.clientX, y: event.clientY };
          setBoxes(state.working);
          const k = camera.k;
          const onMove = (move: PointerEvent): void => {
            if (!state.moved && Math.hypot(move.clientX - state.x, move.clientY - state.y) < 4) return;
            state.moved = true;
            const dx = (move.clientX - state.x) / k;
            const dy = (move.clientY - state.y) / k;
            const candidate =
              mode === 'move'
                ? { ...rule.box, x: rule.box.x + dx, y: rule.box.y + dy }
                : { ...rule.box, w: rule.box.w + dx, h: rule.box.h + dy };
            state.working = { ...state.working, [key]: fit(rule, mode, candidate, state.working[key] ?? rule.box) };
            setBoxes(state.working);
          };
          const onUp = (): void => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            window.removeEventListener('pointercancel', onUp);
            if (!state.moved) return;
            // 머리줄의 이름을 잡고 끌었어도 놓는 순간의 클릭은 고르기가 아니다.
            draggedAt.current = performance.now();
            persist(state.working);
          };
          window.addEventListener('pointermove', onMove);
          window.addEventListener('pointerup', onUp);
          window.addEventListener('pointercancel', onUp);
        },
        nudge: (key, event) => {
          const step: Record<string, [number, number]> = {
            ArrowLeft: [-8, 0],
            ArrowRight: [8, 0],
            ArrowUp: [0, -8],
            ArrowDown: [0, 8],
          };
          const delta = step[event.key];
          if (!delta || !event.altKey) return;
          const rule = rules.get(key);
          if (!rule) return;
          event.preventDefault();
          event.stopPropagation();
          const mode = event.shiftKey ? 'resize' : 'move';
          const candidate =
            mode === 'move'
              ? { ...rule.box, x: rule.box.x + delta[0], y: rule.box.y + delta[1] }
              : { ...rule.box, w: rule.box.w + delta[0], h: rule.box.h + delta[1] };
          const next = { ...freeze(rule, key), [key]: fit(rule, mode, candidate, rule.box) };
          setBoxes(next);
          persist(next, 500);
        },
      }
    : null;

  /** 떠 있는 판이 가린 가장자리. 넓은 화면은 메뉴가 왼쪽·도구 줄이 위, 좁은 화면은 둘 다 위. */
  const insets = useCallback((): Insets => {
    const view = viewRef.current;
    const stage = view?.closest('.site-stage');
    if (!view || !stage) return { top: 0, right: 0, bottom: 0, left: 0 };
    const box = view.getBoundingClientRect();
    const nav = stage.querySelector('.monitor-overlay-nav .monitor-nav')?.getBoundingClientRect();
    const top = stage.querySelector('.monitor-overlay-top')?.getBoundingClientRect();
    const detail = stage.querySelector('.site-detail')?.getBoundingClientRect();
    // 오른쪽 아래 배율 단추 — 그림의 아래 끝이 그 밑에 깔리지 않게.
    const controls = stage.querySelector('.draw-controls')?.getBoundingClientRect();
    const under = controls ? box.bottom - controls.top : 0;
    const wide = window.matchMedia(WIDE).matches;
    if (wide) {
      return {
        left: nav ? nav.right - box.left : 0,
        top: top ? top.bottom - box.top : 0,
        right: detail ? box.right - detail.left : 0,
        bottom: under,
      };
    }
    return {
      left: 0,
      top: Math.max(nav ? nav.bottom - box.top : 0, top ? top.bottom - box.top : 0),
      right: 0,
      bottom: detail ? box.bottom - detail.top : under,
    };
  }, []);

  const flyTo = useCallback(
    (id: string | null, maxK: number, animate: boolean): void => {
      const view = viewRef.current;
      const world = worldRef.current;
      if (!view || !world) return;
      const target =
        id === null
          ? null
          : [...world.querySelectorAll<HTMLElement>('[data-focus]')].find((element) => element.dataset.focus === id);
      const rect = target
        ? rectInWorld(target, world)
        : { x: 0, y: 0, width: world.offsetWidth, height: world.offsetHeight };
      const next = fitRect(rect, { width: view.clientWidth, height: view.clientHeight }, insets(), {
        maxK,
        margin: 28,
      });
      window.clearTimeout(flyTimer.current);
      if (animate) {
        setFlying(true);
        flyTimer.current = window.setTimeout(() => setFlying(false), 420);
      }
      fitted.current = next;
      setCamera(next);
    },
    [insets],
  );
  useEffect(() => () => window.clearTimeout(flyTimer.current), []);

  const focusId = focusIdOf(resolved);
  const kind = resolved.scope.kind;
  // 처음 한 번은 다 받은 뒤 날지 않고 놓고, 그다음부터는 고를 때마다 날아간다.
  const ready = settled && boxes !== null;
  useLayoutEffect(() => {
    if (!ready) return;
    flyTo(focusId, FOCUS_MAX_K[kind], placed);
    if (!placed) setPlaced(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, focusId, flights]);

  /*
   * 맞춘 뒤 사람이 손대기 전에 판 크기가 바뀌면 다시 맞춘다 — 창 크기를 바꿨을 때, 그리고 새로 고침에서 자료가 스타일보다
   * 먼저 와 판이 아직 제 크기가 아닐 때 맞춘 첫 자리(배율 15% 로 구석에 놓였다 — 2026-10-22 운영 빌드에서 봤다).
   */
  const refit = useRef(() => {});
  refit.current = () => flyTo(focusId, FOCUS_MAX_K[kind], false);
  useEffect(() => {
    const view = viewRef.current;
    if (!view || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      if (fitted.current && cameraNow.current === fitted.current) refit.current();
    });
    observer.observe(view);
    return () => observer.disconnect();
  }, []);

  // 휠은 그림판이 먹는다 — 본문이 굴러가지 않게(React 의 onWheel 은 막을 수 없는 수동 듣기라 직접 단다).
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    const onWheel = (event: WheelEvent): void => {
      event.preventDefault();
      const box = view.getBoundingClientRect();
      const point = { x: event.clientX - box.left, y: event.clientY - box.top };
      setCamera((current) => zoomAt(current, wheelFactor(event.deltaY, event.deltaMode, event.ctrlKey), point));
    };
    view.addEventListener('wheel', onWheel, { passive: false });
    return () => view.removeEventListener('wheel', onWheel);
  }, []);

  const local = (event: { clientX: number; clientY: number }): { x: number; y: number } => {
    const box = viewRef.current!.getBoundingClientRect();
    return { x: event.clientX - box.left, y: event.clientY - box.top };
  };
  const center = (): { x: number; y: number } => ({
    x: (viewRef.current?.clientWidth ?? 0) / 2,
    y: (viewRef.current?.clientHeight ?? 0) / 2,
  });
  const zoomBy = (factor: number): void => setCamera((current) => zoomAt(current, factor, center()));

  return (
    <>
      <div
        ref={viewRef}
        className={`site-viewport ${panning ? 'is-panning' : ''}`}
        tabIndex={0}
        role="application"
        aria-label="사업장 그림 — 끌어서 옮기고 휠로 키우고 줄입니다. 화살표 이동, + − 배율, 0 전체 보기"
        onPointerDown={(event) => {
          if (event.pointerType === 'mouse' && event.button !== 0) return;
          const point = local(event);
          pointers.current.set(event.pointerId, point);
          if (pointers.current.size === 1) drag.current = { start: point, last: point, moved: false };
        }}
        onPointerMove={(event) => {
          if (!pointers.current.has(event.pointerId)) return;
          const point = local(event);
          if (pointers.current.size >= 2) {
            const [a, b] = [...pointers.current.keys()];
            const before = { a: pointers.current.get(a!)!, b: pointers.current.get(b!)! };
            pointers.current.set(event.pointerId, point);
            const after = { a: pointers.current.get(a!)!, b: pointers.current.get(b!)! };
            setCamera((current) => pinch(current, before, after));
            if (drag.current) drag.current.moved = true;
            return;
          }
          pointers.current.set(event.pointerId, point);
          const state = drag.current;
          if (!state) return;
          // 조금 미끄러진 것은 누르기다 — 5px 를 넘어야 끌기로 본다(노드·제목을 누르려다 판이 밀리지 않게).
          if (!state.moved && Math.hypot(point.x - state.start.x, point.y - state.start.y) < 5) return;
          if (!state.moved) {
            state.moved = true;
            viewRef.current?.setPointerCapture(event.pointerId);
            setPanning(true);
          }
          const dx = point.x - state.last.x;
          const dy = point.y - state.last.y;
          state.last = point;
          setCamera((current) => panBy(current, dx, dy));
        }}
        onPointerUp={(event) => {
          pointers.current.delete(event.pointerId);
          if (pointers.current.size === 0) {
            if (drag.current?.moved) draggedAt.current = performance.now();
            drag.current = null;
            setPanning(false);
          } else {
            const [rest] = [...pointers.current.values()];
            drag.current = rest ? { start: rest, last: rest, moved: true } : null;
          }
        }}
        onPointerCancel={(event) => {
          pointers.current.delete(event.pointerId);
          if (pointers.current.size === 0) {
            drag.current = null;
            setPanning(false);
          }
        }}
        onClickCapture={(event) => {
          // 끌고 난 뒤의 클릭은 누르기가 아니다 — 노드 위에서 끌기를 끝내도 그 화면으로 들어가지 않게.
          if (performance.now() - draggedAt.current > 300) return;
          draggedAt.current = 0;
          event.preventDefault();
          event.stopPropagation();
        }}
        onKeyDown={(event) => {
          if (event.target !== event.currentTarget) return;
          const step = 80;
          const moves: Record<string, [number, number]> = {
            ArrowLeft: [step, 0],
            ArrowRight: [-step, 0],
            ArrowUp: [0, step],
            ArrowDown: [0, -step],
          };
          const move = moves[event.key];
          if (move) {
            event.preventDefault();
            setCamera((current) => panBy(current, move[0], move[1]));
          } else if (event.key === '+' || event.key === '=') {
            event.preventDefault();
            zoomBy(ZOOM_STEP);
          } else if (event.key === '-' || event.key === '_') {
            event.preventDefault();
            zoomBy(1 / ZOOM_STEP);
          } else if (event.key === '0') {
            event.preventDefault();
            onSelect({ kind: 'all' });
          }
        }}
      >
        <div
          ref={worldRef}
          className={`site-world ${flying ? 'is-flying' : ''} ${editing ? 'is-editing' : ''}`}
          style={{
            transform: `translate(${camera.x}px, ${camera.y}px) scale(${camera.k})`,
            visibility: placed ? 'visible' : 'hidden',
            width: layout.width,
            height: layout.height,
          }}
        >
          {layout.regions.map((laid) => (
            <RegionArea
              key={laid.key}
              laid={laid}
              selected={resolved}
              onSelect={onSelect}
              canEdit={canEdit}
              onConfigure={onConfigure}
              edit={edit}
            />
          ))}
        </div>
      </div>

      <div className="draw-controls float-panel" role="group" aria-label="그림 배율">
        <button type="button" aria-label="축소" onClick={() => zoomBy(1 / ZOOM_STEP)}>
          <Minus className="h-3.5 w-3.5" strokeWidth={2.2} />
        </button>
        <button
          type="button"
          className="draw-zoom-value"
          aria-label="100% 로"
          title="100% 로"
          onClick={() => setCamera((current) => zoomAt(current, 1 / current.k, center()))}
        >
          {Math.round(camera.k * 100)}%
        </button>
        <button type="button" aria-label="확대" onClick={() => zoomBy(ZOOM_STEP)}>
          <Plus className="h-3.5 w-3.5" strokeWidth={2.2} />
        </button>
        <button
          type="button"
          className="draw-fit"
          onClick={() =>
            resolved.scope.kind === 'all' ? flyTo(null, FOCUS_MAX_K.all, true) : onSelect({ kind: 'all' })
          }
        >
          <Maximize className="h-3.5 w-3.5" strokeWidth={2.2} />
          전체 보기
        </button>
        {/*
          배치 편집 — 지역·사업장·위치 영역을 옮기고 크기를 바꾼다(내 계정에만 저장).

          켜도 이 막대는 그대로다. 오른쪽에 붙은 막대라 편집 때만 단추를 하나 더 달면 [편집 완료] 가 그만큼 왼쪽으로
          밀려, 끄려고 같은 자리를 누르면 허공을 누른다. 편집 때만 쓰는 [기본 배치로] 는 아래 "배치 편집 중" 안내에 둔다.
        */}
        <button
          type="button"
          className={editing ? 'is-on' : undefined}
          onClick={() => setEditing((on) => !on)}
          title="영역을 끌어 옮기고 모서리로 크기를 바꿉니다(내 계정에만 저장)"
        >
          <Move className="h-3.5 w-3.5" strokeWidth={2.2} />
          {editing ? '편집 완료' : '배치 편집'}
        </button>
      </div>
      {editing ? (
        <div className="site-edit-hint float-panel">
          <p role="status">
            <strong>배치 편집 중</strong> · 머리줄을 끌어 옮기고 오른쪽 아래 모서리로 크기를 바꿉니다. 영역은 상위 영역
            밖으로 나가지 않습니다.
            {saveError ? <span className="text-rose-700"> · 저장하지 못했습니다: {saveError}</span> : null}
          </p>
          <button
            type="button"
            className="btn btn-tiny"
            title="옮기고 키운 것을 모두 지우고 자동 배치로 돌아갑니다"
            onClick={() => {
              setBoxes({});
              persist({});
            }}
          >
            <RotateCcw className="h-3.5 w-3.5" strokeWidth={2.2} />
            기본 배치로
          </button>
        </div>
      ) : null}
    </>
  );
}

function AreaHead({
  icon,
  title,
  sub,
  counts,
  stale,
  onSelect,
  extra,
  areaKey,
  edit,
}: {
  icon: ReactNode;
  title: string;
  sub?: ReactNode;
  counts: Counts;
  stale: boolean;
  onSelect: () => void;
  extra?: ReactNode;
  areaKey: string;
  edit: AreaEdit | null;
}): ReactNode {
  return (
    // 편집 중에는 머리줄 전체가 옮기는 손잡이다. 이름 단추를 잡고 끌어도 된다(놓을 때의 클릭은 삼킨다).
    <header className="site-area-head" onPointerDown={edit ? (event) => edit.start(areaKey, 'move', event) : undefined}>
      <button
        type="button"
        className="site-area-title"
        onClick={onSelect}
        onKeyDown={edit ? (event) => edit.nudge(areaKey, event) : undefined}
      >
        <span className="shrink-0 text-slate-400">{icon}</span>
        <span className="truncate">{title}</span>
      </button>
      {extra}
      {sub ? <span className="min-w-0 truncate text-[11.5px] text-slate-500">{sub}</span> : null}
      <span className="ml-auto flex shrink-0 items-center gap-2">
        <CountLine counts={counts} stale={stale} />
        <HealthBar counts={counts} stale={stale} className="w-16" />
      </span>
    </header>
  );
}

/** 크기 손잡이 — 오른쪽 아래 모서리. 편집 중에만. */
function ResizeHandle({ areaKey, edit }: { areaKey: string; edit: AreaEdit | null }): ReactNode {
  if (!edit) return null;
  return (
    <span
      className="site-resize"
      aria-hidden="true"
      title="끌어서 크기 조절"
      onPointerDown={(event) => edit.start(areaKey, 'resize', event)}
    />
  );
}

function RegionArea({
  laid,
  selected,
  onSelect,
  canEdit,
  onConfigure,
  edit,
}: {
  laid: LaidRegion;
  selected: ResolvedScope;
  onSelect: (scope: SiteScope) => void;
  canEdit: boolean;
  onConfigure: () => void;
  edit: AreaEdit | null;
}): ReactNode {
  const labels = useSiteLabels();
  const region = laid.region;
  const on = selected.scope.kind === 'region' && selected.region?.key === region.key;
  return (
    <section
      className={`site-area site-box is-region ${on ? 'is-selected' : ''}`}
      data-focus={`r:${region.key}`}
      style={boxStyle(laid.box)}
    >
      <AreaHead
        icon={<MapPin className="h-4 w-4" strokeWidth={2} />}
        title={region.name}
        counts={region}
        stale={region.stale}
        onSelect={() => onSelect({ kind: 'region', region: region.name })}
        areaKey={laid.key}
        edit={edit}
        extra={
          region.name === unnamedRegion(labels) && canEdit && !edit ? (
            <button type="button" className="link text-[12px]" onClick={onConfigure}>
              {labels.region} 지정
            </button>
          ) : null
        }
      />
      {laid.factories.map((factory) => (
        <FactoryArea key={factory.key} laid={factory} selected={selected} onSelect={onSelect} edit={edit} />
      ))}
      <ResizeHandle areaKey={laid.key} edit={edit} />
    </section>
  );
}

function FactoryArea({
  laid,
  selected,
  onSelect,
  edit,
}: {
  laid: LaidFactory;
  selected: ResolvedScope;
  onSelect: (scope: SiteScope) => void;
  edit: AreaEdit | null;
}): ReactNode {
  const factory = laid.factory;
  const pick = (display: SiteDisplay): void =>
    onSelect({ kind: 'display', region: factory.region, site: factory.name, display: display.key });
  const on = selected.scope.kind === 'factory' && selected.factory?.key === factory.key;
  /*
    어느 서버의 화면인가 — 공장 이름이 곧 서버 이름이고 연결도 멀쩡하면(아직 이름을 붙이지 않았다) 같은 말을 두 번
    쓰지 않는다.
  */
  const showSources = factory.sources.some(
    (source) => source.stale || factory.sources.length > 1 || sourceLabel(source) !== factory.name,
  );
  return (
    <section
      className={`site-area site-box is-factory ${on ? 'is-selected' : ''} ${factory.stale ? 'is-stale' : healthTone(factory) === 'ok' ? '' : 'is-warn'}`}
      data-focus={`f:${factory.key}`}
      style={boxStyle(laid.box)}
    >
      <AreaHead
        icon={<Building className="h-4 w-4" strokeWidth={2} />}
        title={factory.name}
        counts={factory}
        stale={factory.stale}
        onSelect={() => onSelect({ kind: 'factory', region: factory.region, site: factory.name })}
        areaKey={laid.key}
        edit={edit}
        sub={
          showSources ? (
            <span className="inline-flex items-center gap-1.5">
              {factory.sources.map((source) => (
                <span key={source.id} className="inline-flex items-center gap-1">
                  {source.kind === 'remote' && source.stale ? (
                    <WifiOff className="h-3 w-3 text-slate-400" aria-hidden="true" />
                  ) : null}
                  {sourceLabel(source)}
                  {source.kind === 'remote' ? (
                    source.stale ? (
                      <span title={source.asOf ? `${relativeTime(source.asOf)} 기준` : undefined}>· 연결 끊김</span>
                    ) : (
                      ' · 원격 서버'
                    )
                  ) : null}
                </span>
              ))}
            </span>
          ) : null
        }
      />
      {laid.buildings.map((building) => (
        <BuildingZone
          key={building.key}
          laid={building}
          showSource={factory.sources.length > 1}
          selectedKey={selected.display?.key ?? null}
          onPick={pick}
          edit={edit}
        />
      ))}
      <ResizeHandle areaKey={laid.key} edit={edit} />
    </section>
  );
}

/** 건물 — 공장 영역 안의 칸. 이름표 아래에 그 건물의 화면 노드가 폭에 맞춰 줄을 바꿔 선다. */
function BuildingZone({
  laid,
  showSource,
  selectedKey,
  onPick,
  edit,
}: {
  laid: LaidBuilding;
  showSource: boolean;
  selectedKey: string | null;
  onPick: (display: SiteDisplay) => void;
  edit: AreaEdit | null;
}): ReactNode {
  const building = laid.building;
  const stale = building.source.stale;
  const count = building.displays?.length ?? 0;
  return (
    <div className={`site-zone site-box ${stale ? 'is-stale' : ''}`} style={boxStyle(laid.box)}>
      <p className="site-zone-head" onPointerDown={edit ? (event) => edit.start(laid.key, 'move', event) : undefined}>
        <Building2 className="h-3.5 w-3.5 shrink-0 text-slate-400" strokeWidth={2} aria-hidden="true" />
        {edit ? (
          // 편집 중에는 건물 이름도 키보드로 옮길 수 있게 초점을 받는다.
          <button type="button" className="site-zone-name" onKeyDown={(event) => edit.nudge(laid.key, event)}>
            {building.name}
          </button>
        ) : (
          <span className={`truncate ${building.location === '' ? 'text-slate-500' : 'text-slate-700'}`}>
            {building.name}
          </span>
        )}
        {showSource ? <span className="truncate text-slate-400">{sourceLabel(building.source)}</span> : null}
        <span className="ml-auto shrink-0">
          <CountLine counts={building} stale={stale} />
        </span>
      </p>
      {building.displays && count > 0 ? (
        <div className="site-nodes">
          {building.displays.map((display) => (
            <DisplayNode
              key={display.key}
              display={display}
              selected={display.key === selectedKey}
              onPick={() => onPick(display)}
            />
          ))}
        </div>
      ) : (
        <p className="site-zone-note">
          {stale
            ? '연결이 끊겨 화면 목록을 받지 못했습니다 — 숫자는 끊기기 전의 것입니다.'
            : '화면 목록을 받지 못했습니다 — 숫자만 표시합니다.'}
        </p>
      )}
      <ResizeHandle areaKey={laid.key} edit={edit} />
    </div>
  );
}
